import { nameUnit } from '@/validations/validatorUnits'
import { required, maxLength, numeric } from '@vuelidate/validators'
import {
  getHasBlockNumbers,
  getHasFreeSingleNumbers,
  getIsNumberModeBlock,
  getIsValidDirectDial,
  getIsValidQuickDial,
} from './validators'
import router from '@/router'
import type { Config } from './tour'
import { useDevStore } from '@/stores/dev/store'
import { useExtensionsStore } from '@/stores/extensions/store'
import { useCtaStore } from '@/stores/cta/store'
import { useNotificationStore } from '@/stores/notifications/store'
import { computed } from 'vue'
import { i18n } from '@/core/i18n'

/*
  Requirements:
    - Spare license
    - free single or block number
    - Classic or Pro edition, not Basic
  Steps:
    - Add a queue
    - Set incoming call config
    - Set Queue settings
      - Waiting music (waiting music)
      - Timeout
      - Notification channel (no notifications)
    - Show how to assign devices
    - Save
    - Show where extensions are displayed
*/

export const CTAQueueSetupConfig: Config = {
  onStart() {
    // Redirect to overview
    router.push('/').catch((error) => {
      if (error.name !== 'NavigationDuplicated') {
        throw error
      }
    })

    // Force re-render to reset the view (if in edit extensions mode, the add button is hidden)
    useDevStore().forceRerender()
  },
  steps: [
    // Show requirements
    {
      delay: 200, // Initial delay to make sure the onStart has had effect
      title: 'tutorial.cta.queueSetup.intro.title',
      text: 'tutorial.cta.queueSetup.intro.text',
      showNextButton: true,
      waitForElements: [
        '[data-test=overview-section-extension] [data-test=section-container][data-loading=false]',
      ],
      canGoToNext() {
        // Check if is not Basic edition

        const edition = useCtaStore().getEdition

        const isBasicEdition = edition === 'basic'

        if (isBasicEdition) {
          useNotificationStore().add({
            type: 'error',
            title: computed(() =>
              i18n.global.t(
                'tutorial.cta.queueSetup.intro.requirements.basicEdition.invalid'
              )
            ),
          })

          return false
        }

        // Check if needs to upgrade their plan

        const extensions = useExtensionsStore().list

        const ctaData = useCtaStore().get

        const hasMaxExtensions = extensions.length >= (ctaData.maxLines ?? 0)

        const needsToUpgradePlan = hasMaxExtensions

        if (needsToUpgradePlan) {
          useNotificationStore().add({
            type: 'error',
            title: computed(() =>
              i18n.global.t(
                'tutorial.cta.queueSetup.intro.requirements.maxLines.invalid'
              )
            ),
          })

          return false
        }

        // Check if has any usable numbers for the new extension

        const hasBlockNumbers = getHasBlockNumbers()

        const hasFreeSingleNumbers = getHasFreeSingleNumbers()

        if (!hasBlockNumbers && !hasFreeSingleNumbers) {
          useNotificationStore().add({
            type: 'error',
            title: computed(() =>
              i18n.global.t(
                'tutorial.cta.queueSetup.intro.requirements.freeNumbers.invalid'
              )
            ),
          })

          return false
        }

        return true
      },
    },
    // Create an extension: add extension
    {
      title: 'tutorial.cta.queueSetup.addExtension.title',
      text: 'tutorial.cta.queueSetup.addExtension.text',
      attachElement:
        '[data-test=overview-section-extension] [data-test=add-new-section-item]',
      nextOnClickElements: [
        '[data-test=overview-section-extension] [data-test=add-new-section-item]',
      ],
      attachPosition: 'top',
    },
    // Create an extension: set name
    {
      title: 'tutorial.cta.queueSetup.extension.name.title',
      text: 'tutorial.cta.queueSetup.extension.name.text',
      showNextButton: true,
      attachElement: '[data-test=extension-name]',
      attachPosition: 'top',
      waitForElements: ['[data-test=extension-page][data-is-loading=false]'],
      watchValueOfElements: ['[data-test=extension-name]'],
      preventEnterAndTabInElements: ['[data-test=extension-name]'],
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=extension-name]'
        )

        const value = element?.value

        if (
          !nameUnit.required.$validator(value, undefined, undefined) ||
          !nameUnit.minLength.$validator(value, undefined, undefined) ||
          !nameUnit.maxLength.$validator(value, undefined, undefined)
        ) {
          return false
        }

        // Check if name already used

        const extensions = useExtensionsStore().list

        const extensionNames = extensions.map((extension) => {
          return extension.name
        })

        const isNameAlreadyUsed = extensionNames.some(
          (name) => name.toLowerCase() === value?.toLowerCase()
        )

        if (isNameAlreadyUsed) {
          return false
        }

        return true
      },
    },
    // Create an extension: set extension type
    {
      title: 'tutorial.cta.queueSetup.extension.type.title',
      text: 'tutorial.cta.queueSetup.extension.type.text',
      showNextButton: true,
      attachElement: '[data-test=extensionType]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=extensionType]'],
      preventEnterAndTabInElements: ['[data-test=extensionType]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=extensionType]'
        )

        const value = element?.value

        // Check that extension type is set to "Queue"
        return value === 'queue'
      },
    },
    // Create an extension: set number mode
    {
      title: 'tutorial.cta.queueSetup.extension.numberMode.title',
      text: 'tutorial.cta.queueSetup.extension.numberMode.text',
      showNextButton: true,
      attachElement: '[data-test=numberMode]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=numberMode]'],
      preventEnterAndTabInElements: ['[data-test=numberMode]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=numberMode]'
        )

        const value = element?.value

        switch (value) {
          case 'false':
            return getHasBlockNumbers()

          case 'true':
            return getHasFreeSingleNumbers()
        }

        return false
      },
    },
    // Create an extension: set phone number
    {
      title: 'tutorial.cta.queueSetup.extension.phoneNumber.title',
      text: 'tutorial.cta.queueSetup.extension.phoneNumber.text',
      showNextButton: true,
      attachElement: '[data-test=phone-number]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=phone-number]'],
      preventEnterAndTabInElements: ['[data-test=phone-number]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=phone-number]'
        )

        const value = element?.value

        return required.$validator(value, undefined, undefined) as boolean
      },
    },
    // Create an extension: set direct dial (only for Block numbers)
    {
      title: 'tutorial.cta.queueSetup.extension.directDial.title',
      text: 'tutorial.cta.queueSetup.extension.directDial.text',
      showNextButton: true,
      attachElement: '[data-test=dial-options]',
      attachPosition: 'top',
      showOn: getIsNumberModeBlock,
      watchValueOfElements: [
        '[data-test=direct-dial]',
        '#directDialIsQuickDial',
        '[data-test=quick-dial]',
      ],
      preventEnterAndTabInElements: [
        '[data-test=direct-dial]',
        '#directDialIsQuickDial',
        '[data-test=quick-dial]',
      ],
      canGoToNext() {
        const directDialElement = document.querySelector<HTMLInputElement>(
          '[data-test=direct-dial]'
        )

        const directDial = directDialElement?.value

        const quickDialElement = document.querySelector<HTMLInputElement>(
          '[data-test=quick-dial]'
        )

        const quickDial = quickDialElement?.value

        const trunkSelector = document.querySelector<HTMLSelectElement>(
          '[data-test=phone-number]'
        )

        const trunkId = trunkSelector?.value

        const isDirectDialValid = getIsValidDirectDial(
          directDial ?? '',
          trunkId ?? ''
        )

        const isQuickDialValid = getIsValidQuickDial(quickDial ?? '')

        return isDirectDialValid && isQuickDialValid
      },
    },
    // Create an extension: set quick dial (only for single numbers)
    {
      title: 'tutorial.cta.queueSetup.extension.quickDial.title',
      text: 'tutorial.cta.queueSetup.extension.quickDial.text',
      showNextButton: true,
      attachElement: '[data-test=quick-dial]',
      attachPosition: 'top',
      showOn() {
        const isBlockMode = getIsNumberModeBlock()

        return !isBlockMode
      },
      watchValueOfElements: ['[data-test=quick-dial]'],
      preventEnterAndTabInElements: ['[data-test=quick-dial]'],
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=quick-dial]'
        )

        const value = element?.value

        return getIsValidQuickDial(value ?? '')
      },
    },
    // Create an extension: set waiting music
    {
      title: 'tutorial.cta.queueSetup.extension.waitingMusice.title',
      text: 'tutorial.cta.queueSetup.extension.waitingMusice.text',
      showNextButton: true,
      attachElement: '[data-test=queue-audiofile]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=queue-audiofile]'],
      preventEnterAndTabInElements: ['[data-test=queue-audiofile]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=queue-audiofile]'
        )

        const value = element?.value

        return required.$validator(value, undefined, undefined) as boolean
      },
    },
    // Create an extension: set timeout
    {
      title: 'tutorial.cta.queueSetup.extension.timeout.title',
      text: 'tutorial.cta.queueSetup.extension.timeout.text',
      showNextButton: true,
      attachElement: '[data-test=queue-timeout]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=queue-timeout]'],
      preventEnterAndTabInElements: ['[data-test=queue-timeout]'],
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=queue-timeout]'
        )

        const value = element?.value

        if (
          !required.$validator(value, undefined, undefined) ||
          !numeric.$validator(value, undefined, undefined) ||
          !maxLength(4).$validator(value, undefined, undefined)
        ) {
          return false
        }

        return true
      },
    },
    // Create an extension: set notification channel
    {
      title: 'tutorial.cta.queueSetup.extension.notificationChannel.title',
      text: 'tutorial.cta.queueSetup.extension.notificationChannel.text',
      showNextButton: true,
      attachElement: '[data-test=notification-channel]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=notification-channel]'],
      preventEnterAndTabInElements: ['[data-test=notification-channel]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=notification-channel]'
        )

        const value = element?.value

        // Check that notification channel is set to "No notification"
        return value === 'none'
      },
    },
    // Create an extension: assign devices: open assign devices page
    {
      title: 'tutorial.cta.queueSetup.extension.openAssignDevice.title',
      text: 'tutorial.cta.queueSetup.extension.openAssignDevices.text',
      attachElement: '[data-test=assign-devices]',
      attachPosition: 'top',
      nextOnClickElements: ['[data-test=assign-devices]'],
    },
    // Create an extension: assign devices: select device
    {
      delay: 500, // Wait for AssignDevices to finish it's scroll
      title:
        'tutorial.cta.queueSetup.extension.assignDevice.selectDevice.title',
      text: 'tutorial.cta.queueSetup.extension.assignDevice.selectDevice.text',
      attachElement: '[data-test=assign-devices-section]',
      attachPosition: 'bottom',
      showNextButton: true,
    },
    // Create an extension: assign devices: save changes
    {
      title: 'tutorial.cta.queueSetup.extension.assignDevice.save.title',
      text: 'tutorial.cta.queueSetup.extension.assignDevice.save.text',
      attachElement: '[data-test=action-btn-save]',
      attachPosition: 'top',
      nextOnClickElements: ['[data-test=action-btn-save]'],
    },
    // Create an extension: save changes
    {
      title: 'tutorial.cta.queueSetup.extension.save.title',
      text: 'tutorial.cta.queueSetup.extension.save.text',
      attachElement: '[data-test=main-settings] [data-test=action-btn-save]',
      nextOnClickElements: [
        '[data-test=main-settings] [data-test=action-btn-save]',
      ],
      waitForElements: [
        '[data-test=extension-page][data-is-loading=false] [data-test=main-settings]',
      ],
      attachPosition: 'top',
    },
    // Overview: show where extensions are viewed
    {
      title: 'tutorial.cta.queueSetup.extensionOverview.title',
      text: 'tutorial.cta.queueSetup.extensionOverview.text',
      showFinishButton: true,
      attachElement: '[data-test=overview-section-extension]',
      attachPosition: 'top',
      waitForElements: [
        '[data-test=overview-section-extension] [data-test=section-container][data-loading=false]',
      ],
      preventClickInElements: [
        '[data-test=overview-section-extension] [data-test=show-multi-edit]',
        '[data-test=overview-section-extension] [data-test=add-new-section-item]',
        '[data-test=edit-extension]',
        '[data-test=overview-section-extension] [data-test=open-filter]',
        '[data-test=edit-call-forwarding]',
        '[data-test=edit-wait-loop]',
        '[data-test=catch-all]',
        '[data-test=edit-night-switch]',
        '[data-test*=add-group-]',
        '[data-test*=group-]',
        '[data-test=extension-item-color]',
      ],
    },
  ],
}
